import type { Assignment } from '../types';

interface GeocodeResult {
    lat: number;
    lng: number;
}

// In-memory cache to store geocoded addresses
const geocodeCache = new Map<string, GeocodeResult>();

// Queue for rate limiting
const requestQueue: Array<() => Promise<void>> = [];
let isProcessingQueue = false;

const processQueue = async () => {
    if (isProcessingQueue || requestQueue.length === 0) return;

    isProcessingQueue = true;
    const task = requestQueue.shift();
    if (task) {
        await task();
        // Wait 1.1 seconds before processing next request to respect Nominatim's 1 req/sec limit
        setTimeout(() => {
            isProcessingQueue = false;
            processQueue();
        }, 1100);
    } else {
        isProcessingQueue = false;
    }
};

const enqueueRequest = <T>(request: () => Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
        requestQueue.push(async () => {
            try {
                const result = await request();
                resolve(result);
            } catch (error) {
                reject(error);
            }
        });
        processQueue();
    });
};

export const geocodeAddress = async (address: string): Promise<GeocodeResult | null> => {
    // Check cache first
    if (geocodeCache.has(address)) {
        return geocodeCache.get(address)!;
    }

    return enqueueRequest(async () => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                {
                    headers: {
                        'User-Agent': 'PlannerApp/1.0' // Required by Nominatim
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Geocoding failed');
            }

            const data = await response.json();

            if (data && data.length > 0) {
                const result = {
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon)
                };
                // Cache the result
                geocodeCache.set(address, result);
                return result;
            }
            return null;
        } catch (error) {
            console.error('Error geocoding address:', address, error);
            return null;
        }
    });
};

// Helper to construct full address string from assignment
export const getFullAddress = (assignment: Assignment): string => {
    const parts = [
        assignment.address,
        assignment.postalCode,
        assignment.city
    ].filter(Boolean);

    return parts.join(', ');
};
